import React, { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { API_BASE } from '../config';

const ZONE_CHECK_MS = 90 * 1000;
const WATCH_MIN_INTERVAL_MS = 45 * 1000;
const WATCH_MIN_DISTANCE_METERS = 45;

function haversineMeters(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad((b.latitude || 0) - (a.latitude || 0));
  const dLon = toRad((b.longitude || 0) - (a.longitude || 0));
  const lat1 = toRad(a.latitude || 0);
  const lat2 = toRad(b.latitude || 0);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * While an employee/manager has an active shift (clocked in), periodically POSTs GPS to
 * /api/attendance/presence/zone-check so site changes are logged and supervisors notified.
 * Runs app-wide (not only on the attendance page). Admins are exempt on the server.
 */
export default function ShiftZonePresenceTracker() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const skip =
    role === 'admin' ||
    role === 'super_admin' ||
    !user;

  const clockedInRef = useRef(false);
  const lastPostedAtRef = useRef(0);
  const lastPostedPosRef = useRef(null);

  const refreshClockedIn = useCallback(async () => {
    if (skip) {
      clockedInRef.current = false;
      return false;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        clockedInRef.current = false;
        return false;
      }
      const res = await fetch(`${API_BASE}/api/attendance/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      const on = data.clockedIn === true && data.lastClockType === 'in';
      clockedInRef.current = on;
      return on;
    } catch {
      clockedInRef.current = false;
      return false;
    }
  }, [skip]);

  const postZoneCheck = useCallback(async (pos) => {
    if (skip || !clockedInRef.current || !pos) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await fetch(`${API_BASE}/api/attendance/presence/zone-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: pos })
      });
      lastPostedAtRef.current = Date.now();
      lastPostedPosRef.current = pos;
    } catch (e) {
      console.warn('[ShiftZonePresenceTracker] zone-check', e);
    }
  }, [skip]);

  const runZoneCheck = useCallback(async () => {
    if (skip || !clockedInRef.current) return;
    try {
      const pos = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) =>
            resolve({
              latitude: p.coords.latitude,
              longitude: p.coords.longitude,
              accuracy: p.coords.accuracy
            }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
        );
      });
      if (!pos) return;
      await postZoneCheck(pos);
    } catch (e) {
      console.warn('[ShiftZonePresenceTracker] zone-check', e);
    }
  }, [skip, postZoneCheck]);

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;
    let watchId = null;

    const tick = async () => {
      if (cancelled) return;
      await refreshClockedIn();
      if (cancelled) return;
      await runZoneCheck();
    };

    tick();
    const interval = setInterval(tick, ZONE_CHECK_MS);

    if (navigator.geolocation && navigator.geolocation.watchPosition) {
      watchId = navigator.geolocation.watchPosition(
        async (p) => {
          if (cancelled || !clockedInRef.current) return;
          const nextPos = {
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
            accuracy: p.coords.accuracy
          };
          const now = Date.now();
          const enoughTime = now - lastPostedAtRef.current >= WATCH_MIN_INTERVAL_MS;
          const movedMeters = haversineMeters(lastPostedPosRef.current, nextPos);
          const enoughMove = movedMeters >= WATCH_MIN_DISTANCE_METERS;
          if (enoughTime || enoughMove) {
            await postZoneCheck(nextPos);
          }
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 10000 }
      );
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (watchId != null && navigator.geolocation && navigator.geolocation.clearWatch) {
        try { navigator.geolocation.clearWatch(watchId); } catch (_e) {}
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [skip, refreshClockedIn, runZoneCheck, postZoneCheck]);

  return null;
}
